import { create } from "zustand";
import instance, { setFeatureGateHandler } from "@/lib/axios";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import type { Feature } from "@/types/feature";

interface FeatureState {
  features: Feature[];
  fetchedOrgSlug: string | null;
  isLoading: boolean;
  fetchFeatures: () => Promise<void>;
  invalidate: () => Promise<void>;
  isEnabled: (key: string) => boolean;
  clearFeatureStore: () => void;
}

export const useFeatureStore = create<FeatureState>()((set, get) => ({
  features: [],
  fetchedOrgSlug: null,
  isLoading: false,

  fetchFeatures: async () => {
    let orgSlug = useOrganizationStore.getState().orgSlug;
    if (!orgSlug) {
      await useOrganizationStore.getState().fetchOrgSlug();
      orgSlug = useOrganizationStore.getState().orgSlug;
    }
    if (!orgSlug) return;
    // `fetchedOrgSlug` marks the attempt, not the success — a failed fetch left
    // `features` empty, so a success-shape guard never tripped and this re-ran on
    // every navigation. A genuine org switch changes `orgSlug` and still refetches.
    if (get().fetchedOrgSlug === orgSlug) return;
    if (get().isLoading) return;
    set({ isLoading: true });
    try {
      const { data } = await instance.get<Feature[]>(
        `/api/${orgSlug}/organization/features/`,
      );
      set({ features: data, fetchedOrgSlug: orgSlug, isLoading: false });
    } catch {
      set({ fetchedOrgSlug: orgSlug, isLoading: false });
    }
  },

  /** Reload the list. Only on events that mean it is out of date — the gate's
   * 404 and a role save — never on a timer. */
  invalidate: async () => {
    set({ fetchedOrgSlug: null });
    await get().fetchFeatures();
  },

  isEnabled: (key) => {
    const f = get().features.find((f) => f.key === key);
    return f ? f.is_enabled : true;
  },

  clearFeatureStore: () => {
    set({ features: [], fetchedOrgSlug: null });
  },
}));

// The gate answered "not available" — the list we hold is out of date (the
// caller's role was edited, or the org switched the module off).
setFeatureGateHandler(() => {
  void useFeatureStore.getState().invalidate();
});
